import { useCallback } from 'react';

import { useGoogleOAuthConnect } from '@/hooks/integrations/google/useGoogleOAuthConnect';
import { useMicrosoftOAuthConnect } from '@/hooks/integrations/microsoft/useMicrosoftOAuthConnect';
import { usePermissions } from '@/hooks/usePermissions';
import { useUserProfileData } from '@/hooks/useUserProfileData';
import { Button } from '@/lib/base/buttons/button';
import { Card } from '@/lib/base/card/card';
import { getMicrosoftClientId } from '@/lib/config';
import { cx } from '@/utils/cx';

interface ConnectEmailCardProps {
  orgId: string | undefined;
  className?: string;
}

// Provider-specific presentation for the single underlying connect card.
// The layout below is shared across providers — only this copy/icon varies.
const PROVIDER_COPY = {
  google: {
    icon: '/assets/figma/gmail-icon.svg',
    alt: 'Gmail',
    title: 'Connect your Google account',
    description:
      'Connect Gmail to import your contacts and start getting personalized AI actions',
    button: 'Connect Gmail',
  },
  microsoft: {
    icon: '/assets/figma/outlook-icon.svg',
    alt: 'Outlook',
    title: 'Connect your Microsoft account',
    description:
      'Connect Outlook to import your contacts and start getting personalized AI actions',
    button: 'Connect Outlook',
  },
} as const;

/**
 * CTA card prompting the user to connect their email account. Renders a single
 * shared layout, switching copy + the OAuth connect flow to match the provider
 * the user authenticated with: Microsoft-authenticated users (when a Microsoft
 * client is configured) see the Outlook variant; everyone else sees Gmail.
 * Non-dismissible — persists until an email integration is connected.
 */
export function ConnectEmailCard({ orgId, className }: ConnectEmailCardProps) {
  const { data: profile } = useUserProfileData();
  // Both connect hooks are set up unconditionally (rules-of-hooks); only the
  // selected provider's `connect` is wired to the button.
  const google = useGoogleOAuthConnect(orgId);
  const microsoft = useMicrosoftOAuthConnect(orgId);
  const { hasPermission } = usePermissions();
  const canConnect = hasPermission(
    'ahoy_external_integration',
    'ahoy_object_created'
  );

  const hasMicrosoftLogin =
    profile?.social_accounts?.some(acc => acc.provider === 'microsoft') ??
    false;
  const preferOutlook = hasMicrosoftLogin && Boolean(getMicrosoftClientId());

  const copy = preferOutlook ? PROVIDER_COPY.microsoft : PROVIDER_COPY.google;
  const { connect, isConnecting, error } = preferOutlook ? microsoft : google;

  const handleConnect = useCallback(() => {
    connect();
  }, [connect]);

  return (
    <Card
      variant='default'
      className={cx('h-[395px] flex flex-col overflow-hidden', className)}
    >
      <div className='flex-1 flex flex-col items-center justify-center p-6 text-center'>
        <img src={copy.icon} alt={copy.alt} className='size-12 mb-4' />
        <h2 className='text-xl font-semibold text-[var(--color-text-primary)] mb-2'>
          {copy.title}
        </h2>
        <p className='text-sm text-[var(--color-text-secondary)] mb-6 max-w-xs'>
          {copy.description}
        </p>

        {error && (
          <p
            className='text-sm text-error-600 mb-4'
            role='alert'
            aria-live='polite'
          >
            {error}
          </p>
        )}

        <Button
          size='lg'
          color='primary'
          onClick={handleConnect}
          isDisabled={isConnecting || !orgId || !canConnect}
        >
          {isConnecting ? 'Connecting…' : copy.button}
        </Button>
      </div>
    </Card>
  );
}
